import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  adminListUsers,
  adminCreateUser,
  adminChangeRole,
  adminDeactivateUser,
  adminReactivateUser,
  adminDeleteUser,
  adminResendInvite,
  adminListTenants,
} from "../api/client";
import type { AdminUser, AdminTenant } from "../api/types";
import { useToast } from "../context/ToastContext";
import { RoleEditor, RoleDisplayBadges, rolesFromEditor, type RoleEditorValue } from "./RoleEditor";
import { useAuth } from "../auth/useAuth";
import { ROLE_LABELS } from "../auth/permissions";

const USER_STATE_OPTIONS = [
  { value: "USER_STATE_ACTIVE", label: "Attivo" },
  { value: "USER_STATE_INACTIVE", label: "Disattivato" },
  { value: "USER_STATE_LOCKED", label: "Bloccato" },
  { value: "USER_STATE_INITIAL", label: "Invitato" },
];

function userStateBadge(user: AdminUser) {
  // USER_STATE_INITIAL = account creato, login via magic link (non ha mai impostato una password Zitadel).
  // Al primo login riuscito il metadata `invite_status` viene RIMOSSO (non impostato a
  // "active", vedi _clear_invite_status in magic_link.py) — quindi va trattato come
  // "Invitato" SOLO se e' esplicitamente "pending", non quando e' semplicemente assente.
  // Simmetrico al branch USER_STATE_ACTIVE qui sotto. Bug reale: un utente con login
  // reale confermato (metadata invite_status assente) restava "Invitato" per sempre.
  if (user.state === "USER_STATE_INITIAL") {
    if (user.metadata?.invite_status === "pending") {
      return { label: "Invitato", variant: "info", icon: "mail" };
    }
    return { label: "Attivo", variant: "success", icon: "check_circle" };
  }
  if (user.state === "USER_STATE_ACTIVE" && user.metadata?.invite_status === "pending") {
    return { label: "Invitato", variant: "info", icon: "mail" };
  }
  if (user.state === "USER_STATE_ACTIVE") {
    return { label: "Attivo", variant: "success", icon: "check_circle" };
  }
  if (user.state === "USER_STATE_INACTIVE") {
    return { label: "Disattivato", variant: "neutral", icon: "person_off" };
  }
  if (user.state === "USER_STATE_LOCKED") {
    return { label: "Bloccato", variant: "danger", icon: "lock" };
  }
  return { label: user.state, variant: "neutral", icon: "help" };
}

function formatDate(iso: string | null | undefined) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function roleLabel(roles: string[], tenants: AdminTenant[], tenantId?: string) {
  if (!roles || roles.length === 0) return <span className="text-muted">—</span>;
  const mods = tenants.find(t => t.tenant_id === tenantId)?.modules ?? [];
  return <RoleDisplayBadges roles={roles} tenantModules={mods} />;
}

// Cliente e' sempre mostrato in colonna propria (mai unito al Ruolo/flusso):
// un platform_admin non ha tenant -> "—"; un utente di tenant mostra sempre
// il nome cliente, indipendentemente da quanti flussi/ruoli ha.
function tenantLabel(tenantId: string | undefined, tenants: AdminTenant[]) {
  if (!tenantId) return <span className="text-muted">—</span>;
  const name = tenants.find(t => t.tenant_id === tenantId)?.name;
  return <span>{name || tenantId}</span>;
}

export function AdminUsers() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [tenants, setTenants] = useState<AdminTenant[]>([]);
  const [filterTenant, setFilterTenant] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filterRole, setFilterRole] = useState("");
  const [filterState, setFilterState] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<AdminUser | null>(null);
  const [deleteInput, setDeleteInput] = useState("");
  const toast = useToast();
  const navigate = useNavigate();
  const { user: caller } = useAuth();

  const loadTenants = useCallback(async () => {
    try { setTenants(await adminListTenants()); } catch { /* ignore */ }
  }, []);

  // Debounce della ricerca testuale (~300ms) prima di ricaricare la lista.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await adminListUsers({
        tenantId: filterTenant || undefined,
        search: debouncedSearch || undefined,
        role: filterRole || undefined,
        state: filterState || undefined,
      });
      setUsers(data.users);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [filterTenant, debouncedSearch, filterRole, filterState]);

  useEffect(() => { loadTenants(); }, [loadTenants]);
  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleDeactivate = async (user: AdminUser) => {
    if (!confirm(`Disattivare "${user.display_name || user.email}"?`)) return;
    try {
      await adminDeactivateUser(user.user_id);
      toast.addToast("success", `Utente disattivato: ${user.email}`);
      loadUsers();
    } catch (err) { toast.addToast("error", (err as Error).message); }
  };

  const handleReactivate = async (user: AdminUser) => {
    try {
      await adminReactivateUser(user.user_id);
      toast.addToast("success", `Utente riattivato: ${user.email}`);
      loadUsers();
    } catch (err) { toast.addToast("error", (err as Error).message); }
  };

  const handleResendInvite = async (user: AdminUser) => {
    try {
      await adminResendInvite(user.user_id);
      toast.addToast("success", `Invito re-inviato a ${user.email}`);
    } catch (err) { toast.addToast("error", (err as Error).message); }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await adminDeleteUser(deleteConfirm.user_id);
      toast.addToast("success", `Utente eliminato: ${deleteConfirm.email}`);
      setDeleteConfirm(null);
      setDeleteInput("");
      loadUsers();
    } catch (err) { toast.addToast("error", (err as Error).message); }
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">
          <span className="material-symbols-outlined icon-xl" style={{ marginRight: 8 }}>group</span>
          Gestione Utenti
        </h1>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>person_add</span>
          Nuovo utente
        </button>
      </div>

      {/* Filtri */}
      <div style={{ marginBottom: "1rem", display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
        <div style={{ position: "relative" }}>
          <span className="material-symbols-outlined" style={{
            position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)",
            fontSize: 18, color: "var(--indaco-gray-400)", pointerEvents: "none",
          }}>search</span>
          <input
            className="form-input"
            style={{ width: "auto", minWidth: 240, paddingLeft: 32 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca per nome o email…"
          />
        </div>
        <label className="form-label" style={{ margin: 0 }}>Cliente:</label>
        <select className="form-select" style={{ width: "auto", minWidth: 200 }} value={filterTenant} onChange={(e) => setFilterTenant(e.target.value)}>
          <option value="">Tutti</option>
          {tenants.map((t) => (
            <option key={t.tenant_id} value={t.tenant_id}>{t.name}</option>
          ))}
        </select>
        <label className="form-label" style={{ margin: 0 }}>Ruolo:</label>
        <select className="form-select" style={{ width: "auto", minWidth: 180 }} value={filterRole} onChange={(e) => setFilterRole(e.target.value)}>
          <option value="">Tutti i ruoli</option>
          {Object.entries(ROLE_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
        <label className="form-label" style={{ margin: 0 }}>Stato:</label>
        <select className="form-select" style={{ width: "auto", minWidth: 160 }} value={filterState} onChange={(e) => setFilterState(e.target.value)}>
          <option value="">Tutti gli stati</option>
          {USER_STATE_OPTIONS.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
      </div>

      {error && (
        <div className="card" style={{ borderLeft: "4px solid var(--indaco-danger)", marginBottom: "var(--space-4)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--indaco-danger)" }}>
            <span className="material-symbols-outlined">error</span>
            {error}
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: "center", padding: "2rem" }}><span className="spinner spinner-lg" /></div>
      ) : users.length === 0 ? (
        <div className="card" style={{ textAlign: "center", color: "var(--indaco-gray-500)" }}>Nessun utente trovato</div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          <table className="table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Email</th>
                <th>Cliente</th>
                <th>Ruolo</th>
                <th>Stato</th>
                <th>Creato il</th>
                <th style={{ width: 140 }}>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const st = userStateBadge(u);
                return (
                  <tr key={u.user_id} style={{ cursor: "pointer" }} onClick={() => navigate(`/admin/users/${u.user_id}`)}>
                    <td>
                      <div style={{ fontWeight: 500 }}>{u.display_name || `${u.first_name} ${u.last_name}`}</div>
                    </td>
                    <td>{u.email}</td>
                    <td className="text-sm">{tenantLabel(u.metadata?.tenant_id ?? u.tenant_id, tenants)}</td>
                    <td><span className="text-sm">{roleLabel(u.roles, tenants, u.metadata?.tenant_id ?? u.tenant_id)}</span></td>
                    <td>
                      <span className={`badge badge-${st.variant}`} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                        <span className="material-symbols-outlined" style={{ fontSize: 14 }}>{st.icon}</span>
                        {st.label}
                      </span>
                    </td>
                    <td className="text-sm">{formatDate(u.created_at)}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div style={{ display: "flex", gap: 2 }}>
                        {u.metadata?.invite_status === "pending" && (
                          <button className="btn btn-ghost btn-sm" title="Reinvia invito" onClick={() => handleResendInvite(u)}>
                            <span className="material-symbols-outlined" style={{ fontSize: 18, color: "var(--indaco-info)" }}>send</span>
                          </button>
                        )}
                        {u.state === "USER_STATE_ACTIVE" && (
                          <button className="btn btn-ghost btn-sm" title="Disattiva" onClick={() => handleDeactivate(u)}>
                            <span className="material-symbols-outlined" style={{ fontSize: 18, color: "var(--indaco-warning)" }}>person_off</span>
                          </button>
                        )}
                        {u.state === "USER_STATE_INACTIVE" && (
                          <button className="btn btn-ghost btn-sm" title="Riattiva" onClick={() => handleReactivate(u)}>
                            <span className="material-symbols-outlined" style={{ fontSize: 18, color: "var(--indaco-success)" }}>person</span>
                          </button>
                        )}
                        <button
                          className="btn btn-ghost btn-sm"
                          title={u.user_id === caller?.sub ? "Non puoi eliminare te stesso" : "Elimina"}
                          disabled={u.user_id === caller?.sub}
                          onClick={() => { setDeleteConfirm(u); setDeleteInput(""); }}
                        >
                          <span className="material-symbols-outlined" style={{ fontSize: 18, color: "var(--indaco-danger)" }}>delete</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <CreateUserDialog tenants={tenants} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); loadUsers(); }} />
      )}

      {deleteConfirm && (
        <DeleteConfirmDialog
          user={deleteConfirm}
          input={deleteInput}
          onInputChange={setDeleteInput}
          onConfirm={handleDelete}
          onClose={() => { setDeleteConfirm(null); setDeleteInput(""); }}
        />
      )}
    </div>
  );
}

/* ── Delete confirmation dialog ── */

function DeleteConfirmDialog({
  user, input, onInputChange, onConfirm, onClose,
}: {
  user: AdminUser; input: string; onInputChange: (v: string) => void;
  onConfirm: () => void; onClose: () => void;
}) {
  const canDelete = input === user.email;
  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog-card" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h2 style={{ color: "var(--indaco-danger)" }}>
            <span className="material-symbols-outlined" style={{ fontSize: 22, marginRight: 6, verticalAlign: "middle" }}>warning</span>
            Elimina utente
          </h2>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>
        <p style={{ margin: "0 0 1rem" }}>
          Stai per eliminare <strong>{user.display_name || user.email}</strong> permanentemente.
          Questa azione è <strong>irreversibile</strong>.
        </p>
        <div className="form-group">
          <label className="form-label">Scrivi <code>{user.email}</code> per confermare:</label>
          <input className="form-input" value={input} onChange={(e) => onInputChange(e.target.value)} placeholder={user.email} autoFocus />
        </div>
        <div className="dialog-footer">
          <button className="btn btn-ghost" onClick={onClose}>Annulla</button>
          <button className="btn btn-danger" disabled={!canDelete} onClick={onConfirm}>
            <span className="material-symbols-outlined" style={{ fontSize: 18, marginRight: 4 }}>delete_forever</span>
            Elimina definitivamente
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Create dialog ── */

function CreateUserDialog({
  tenants, onClose, onCreated,
}: {
  tenants: AdminTenant[]; onClose: () => void; onCreated: () => void;
}) {
  const { user: caller } = useAuth();
  const isPlatformAdmin = caller?.roles?.includes("platform_admin") ?? false;

  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [tenantId, setTenantId] = useState(isPlatformAdmin ? "" : (caller?.tenant_id ?? ""));
  const [roleValue, setRoleValue] = useState<RoleEditorValue>({ base: "operator", flows: [] });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const toast = useToast();

  // Quando cambia il tenant, resetta i flussi selezionati
  const handleTenantChange = (newTenantId: string) => {
    setTenantId(newTenantId);
    setRoleValue({ base: roleValue.base, flows: [] });
  };

  const selectedTenant = tenants.find(t => t.tenant_id === tenantId);
  const roles = rolesFromEditor(roleValue);
  const canSubmit = email && firstName && lastName && roles.length > 0 &&
    (roleValue.base !== "operator" || (selectedTenant?.modules?.length ?? 0) <= 1 || roleValue.flows.length > 0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    setError("");
    try {
      const [primaryRole, ...extraRoles] = roles;
      const result = await adminCreateUser({
        email: email.trim(), first_name: firstName.trim(), last_name: lastName.trim(),
        tenant_id: tenantId || undefined, role: primaryRole,
      });
      for (const r of extraRoles) {
        try { await adminChangeRole(result.user_id, r, true); } catch { /* ignora errori parziali */ }
      }
      toast.addToast("success", `Utente creato: ${email}. Potrà accedere via magic link.`);
      onCreated();
    } catch (err) { setError((err as Error).message); }
    finally { setSaving(false); }
  };

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog-card" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h2>Nuovo utente</h2>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><span className="material-symbols-outlined">close</span></button>
        </div>
        <form onSubmit={handleSubmit}>
          {/* Anagrafica */}
          <div style={{ display: "flex", gap: "0.75rem" }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Nome</label>
              <input className="form-input" value={firstName} onChange={(e) => setFirstName(e.target.value)} required autoFocus />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Cognome</label>
              <input className="form-input" value={lastName} onChange={(e) => setLastName(e.target.value)} required />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input className="form-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nome@azienda.it" required />
          </div>

          {/* Cliente (solo platform_admin può sceglierlo) */}
          {isPlatformAdmin && (
            <div className="form-group">
              <label className="form-label">Cliente</label>
              <select className="form-input" value={tenantId} onChange={(e) => handleTenantChange(e.target.value)}>
                <option value="">— Platform Admin (nessun cliente) —</option>
                {tenants.map((t) => (<option key={t.tenant_id} value={t.tenant_id}>{t.name}</option>))}
              </select>
            </div>
          )}

          {/* Ruolo — contestualizzato al cliente scelto */}
          <div className="form-group">
            <label className="form-label">Ruolo</label>
            <RoleEditor
              value={roleValue}
              onChange={setRoleValue}
              tenants={tenants}
              currentTenantId={tenantId}
              isPlatformAdmin={isPlatformAdmin && !tenantId}
            />
          </div>

          {error && <div style={{ color: "var(--indaco-danger)", fontSize: "var(--font-size-sm)", marginBottom: "var(--space-3)" }}>{error}</div>}
          <div className="dialog-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Annulla</button>
            <button type="submit" className="btn btn-primary" disabled={saving || !canSubmit}>
              {saving ? <><span className="spinner" style={{ marginRight: 6 }} />Creazione…</> : "Crea utente"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
